import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from '../useLatest'
import { useBruktbilFilters } from '../useBruktbilFilters'
import { fetchBilAlder, fetchKjoperAlder, fetchMerkeOptions } from './api'
import type { AlderResponse, MerkeOption } from './api'
import AgeBucketChart, { type DisplayMode } from './AgeBucketChart'
import AgeStatsGrid from './AgeStatsGrid'
import SubTabBar from '../SubTabBar'

type TabSlug = 'bil-alder' | 'kjoper-alder'

const TABS = [
  { slug: 'bil-alder', labelKey: 'aldersprofil.tabs.bilAlder', titleKey: 'aldersprofil.titles.bilAlder' },
  { slug: 'kjoper-alder', labelKey: 'aldersprofil.tabs.kjoperAlder', titleKey: 'aldersprofil.titles.kjoperAlder' },
] as const satisfies readonly { slug: TabSlug; labelKey: string; titleKey: string }[]

const MAX_MERKER = 5

// Buyer-age buckets <18 and 90+ are real but rare — pulled out of the line
// chart and shown as side-stats so the main 18..89 curve stays readable.
const KJOPER_ALDER_OUTLIERS = ['<18 år', '90+ år']

// Vehicle-age: the open-ended "20+ år" bucket is pulled off the line too, so
// "Utenfor diagram" in the stats grid is literally true on both sub-tabs.
const BIL_ALDER_OUTLIERS = ['20+ år']

export default function AldersprofilPage() {
  const { t } = useTranslation('bruktbil')
  const {
    from, to, klasse, varebil, omregistrering, selgerPostnummer, kjoperPostnummer, tab, setFilters,
  } = useBruktbilFilters()
  const activeTab = (tab as TabSlug) ?? 'bil-alder'
  const activeTabDef = TABS.find(td => td.slug === activeTab) ?? TABS[0]

  const [bilData, setBilData] = useState<AlderResponse | null>(null)
  const [kjoperData, setKjoperData] = useState<AlderResponse | null>(null)
  const [loading, setLoading] = useState(false)

  const [merkeOptions, setMerkeOptions] = useState<MerkeOption[]>([])
  const [selectedMerker, setSelectedMerker] = useState<string[]>([])
  const [mode, setMode] = useState<DisplayMode>('antall')

  const klasseKey = JSON.stringify(klasse)
  const merkerKey = JSON.stringify(selectedMerker)

  // Mine postnummer (#524): api.ts has always built selger_postnummer /
  // kjoper_postnummer, but the page never passed them, so the layout's
  // postnummer filter silently did nothing here. Same shape as Kjopere/index.tsx.
  const globalFilters = {
    from, to, klasse, varebil, omregistrering, selgerPostnummer, kjoperPostnummer,
  }

  // Load merke options from the same global window the chart uses.
  useLatest(alive => {
    fetchMerkeOptions(globalFilters)
      .then(r => { if (alive()) setMerkeOptions(r.data.rows.filter(o => o.merke)) })
      .catch(() => { if (alive()) setMerkeOptions([]) })
  }, [from, to, klasseKey, varebil, omregistrering, selgerPostnummer, kjoperPostnummer])

  useLatest(alive => {
    setLoading(true)
    const fetcher = activeTab === 'bil-alder' ? fetchBilAlder : fetchKjoperAlder
    fetcher(globalFilters, selectedMerker)
      .then(r => {
        if (!alive()) return
        if (activeTab === 'bil-alder') setBilData(r.data)
        else setKjoperData(r.data)
      })
      .finally(() => { if (alive()) setLoading(false) })
  }, [
    from, to, klasseKey, varebil, omregistrering, selgerPostnummer, kjoperPostnummer,
    activeTab, merkerKey,
  ])

  const activeData = activeTab === 'bil-alder' ? bilData : kjoperData
  const series = activeData?.series ?? []
  const outlierLabels = activeTab === 'kjoper-alder' ? KJOPER_ALDER_OUTLIERS : BIL_ALDER_OUTLIERS

  const sortedOptions = useMemo(
    () => [...merkeOptions].sort((a, b) => (a.merke ?? '').localeCompare(b.merke ?? '', 'nb-NO')),
    [merkeOptions],
  )

  function toggleMerke(m: string) {
    setSelectedMerker(prev => {
      if (prev.includes(m)) return prev.filter(x => x !== m)
      if (prev.length >= MAX_MERKER) return prev
      return [...prev, m]
    })
  }

  function clearMerker() {
    setSelectedMerker([])
  }

  const merkeSub = selectedMerker.length === 0
    ? t('aldersprofil.helUtvalget')
    : selectedMerker.length === 1
      ? t('aldersprofil.sammenlignerEtt', { n: selectedMerker.length })
      : t('aldersprofil.sammenlignerFlere', { n: selectedMerker.length })
  // With a klasse split the backend collapses the merke dimension and returns
  // one aggregate line plus the 4 biggest klasser (#528). Valgte merker filters
  // which rows are counted, but no longer draws a line of its own.
  const subtitle = klasse.length > 0
    ? t('aldersprofil.medKlasser', { sub: merkeSub })
    : merkeSub

  return (
    <div className="space-y-3">
      <SubTabBar
        parent={t('layout.tabs.aldersprofil')}
        tabs={TABS.map(td => ({ key: td.slug, label: t(td.labelKey) }))}
        active={activeTab}
        onChange={slug => setFilters({ tab: slug })}
        testIdPrefix="aldersprofil-tab-"
      />

      <div className="card p-3 space-y-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{t('merkeModell.perUke.yAkse')}</span>
            <div className="flex rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden">
              {(['antall', 'andel'] as DisplayMode[]).map(m => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  data-mode={m}
                  className={`px-3 py-1 text-xs font-medium transition-colors ${
                    mode === m
                      ? 'bg-primary-500 text-white'
                      : 'bg-white dark:bg-dark-card text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-hover'
                  }`}
                >
                  {m === 'antall' ? t('common.antall') : t('merkeModell.perUke.andel')}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500">{t('aldersprofil.merke')}</span>
          <select
            data-testid="merke-picker"
            value=""
            onChange={e => {
              const v = e.target.value
              if (v) toggleMerke(v)
              e.currentTarget.selectedIndex = 0
            }}
            disabled={selectedMerker.length >= MAX_MERKER}
            className="text-xs px-2 py-1 rounded-sm border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card text-gray-700 dark:text-gray-200 disabled:opacity-40"
          >
            <option value="">
              {selectedMerker.length >= MAX_MERKER
                ? t('aldersprofil.maksMerker', { max: MAX_MERKER })
                : t('aldersprofil.leggTilMerke')}
            </option>
            {sortedOptions
              .filter(o => o.merke && !selectedMerker.includes(o.merke))
              .map(o => (
                <option key={o.merke!} value={o.merke!}>
                  {o.merke} ({o.count.toLocaleString('nb-NO')})
                </option>
              ))}
          </select>
          {selectedMerker.map((m, idx) => (
            <span
              key={m}
              data-testid="merke-chip"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
              style={{
                backgroundColor: `${SERIES_COLOR_BG[idx % SERIES_COLOR_BG.length]}`,
                color: SERIES_COLOR_FG[idx % SERIES_COLOR_FG.length],
              }}
            >
              {m}
              <button
                onClick={() => toggleMerke(m)}
                aria-label={t('merkeMultiSelect.fjern', { name: m })}
                className="hover:opacity-70"
              >
                ×
              </button>
            </span>
          ))}
          {selectedMerker.length > 0 && (
            <button
              onClick={clearMerker}
              className="text-xs text-gray-500 underline hover:text-gray-700"
            >
              {t('layout.nullstill')}
            </button>
          )}
        </div>
      </div>

      <AgeBucketChart
        series={series}
        loading={loading || activeData === null}
        title={t(activeTabDef.titleKey)}
        mode={mode}
        outlierLabels={outlierLabels}
      />
      {!loading && activeData !== null && (
        <AgeStatsGrid series={series} outlierLabels={outlierLabels} />
      )}
    </div>
  )
}

// Light/dark-neutral chip backgrounds. Kept in sync (visually) with SERIES_COLORS
// in AgeBucketChart so the chip and its line share an identity.
const SERIES_COLOR_BG = ['#e0f2fe', '#ede9fe', '#cffafe', '#ffedd5', '#d1fae5', '#fee2e2']
const SERIES_COLOR_FG = ['#0369a1', '#6d28d9', '#0e7490', '#9a3412', '#047857', '#b91c1c']
